import { createSelector } from 'reselect';
import {
    getSystemProductionForYear,
    annualSolarValueForYear,
    annualSolarSavings,
    formattedSystemPrice,
    annualCashSavings,
    getYear1CashReturned,
    annualIncentives,
    itcBenefit,
} from '../../../common/solar/selectors';
import {
    getNYRebateTotal,
    getNYPTaxAbatement,
    getNYPTaxAbatementMosaic,
    getNYResiTaxCredit,
} from '../../../common/nyserda/selectors';
import {
    ppaBill,
    formattedPPABill,
    ppaRateForYear,
    annualPPASavings,
    formattedAnnualPPASavings,
} from '../../../common/ppa/selectors';
import {
    annualEnergyValueForYear,
    formattedAnnualBill,
    annualBill,
} from '../../../common/utility/selectors';
import { getITC, getITCCashESS, getMACRS, getOBSITC, getCashITC } from '../../../common/tax/selectors';
import { getCustomLoanPayment, getCustomLoanPaymentESS, annualLoanSavings, getHomeLoanAfterPayment, getHomeLoanAfterPaymentESS, getHomeLoanBeforePayment, getHomeLoanBeforePaymentESS, getAnnualMosaicSavings } from '../../../common/loan/selectors';
import { getOBSAnnualPayment, getAnnualOBSSavings } from '../../../common/obs/selectors';
import { getCAPAnnualPayment, getAnnualCAPSavings, getAnnualCAPIntDeductionValue } from '../../../common/cap/selectors';
import { getSRECs } from '../../../common/srecs/selectors';
import { getSMARTvalue, getMAStateTaxCredit } from '../../../common/smart/selectors';
import { selectSGIPIncentiveValue } from '../../../common/sgip/selectors';

const buildResults = (state) => {
    const { term } = state.ppa;
    const { cashprice } = state.solar;
    const { energyValue } = state.utility;
    const { selectedFinancing, horizonLength } = state.settings;
    const { obsTerm } = state.obs;
    const { capTerm } = state.cap;
    const { mosaicTerm } = state.loan;

    const ppaYears = horizonLength < term ? horizonLength : term;

    const results = {};
    const srecs = getSRECs(state);
    const smart = getSMARTvalue(state);

    for (let year = 1; year <= horizonLength; year += 1) {
        const prevYear = year === 1 ? 1 : year - 1;

        results[year] = {
            systemProduction: getSystemProductionForYear(year, state),
        };
        if (year === 1) {
            results[year].cumulativeSystemProduction = results[year].systemProduction;
        } else {
            results[year].cumulativeSystemProduction =
                results[year].systemProduction + results[prevYear].cumulativeSystemProduction;
        }

        let prevSolarValue = year === 1 ? state.solar.solarValue : 0;
        if (year > 1 && year <= horizonLength) {
            prevSolarValue = results[prevYear].solarValue;
        }
        results[year].solarValue = annualSolarValueForYear(year, prevSolarValue, state);
        results[year].annualSolarSavings = annualSolarSavings(year, prevSolarValue, state);
        if (year === 1) {
            results[year].cumuluativeSolarSavings = results[year].annualSolarSavings;
        } else {
            results[year].cumuluativeSolarSavings =
                results[year].annualSolarSavings + results[prevYear].cumuluativeSolarSavings;
        }
        results[year].cumulativeCashSavings =
            results[year].annualCashSavings + results[prevYear].cumulativeCashSavings;
        results[year].loanPayment = getCustomLoanPayment(state, year);
        results[year].loanPaymentESS = getCustomLoanPaymentESS(state, year);
        results[year].annualLoanSavings = annualLoanSavings(state, prevSolarValue, year);
        results[year].annualMosaicSavings = getAnnualMosaicSavings(state, prevSolarValue, year);
        results[year].recs = srecs[year];
        results[year].smart = smart[year];
        results[year].maStateTaxCredit = getMAStateTaxCredit(state, year);

        // ** MOSAIC ** //

        if (year === 1) {
            results[year].annualHomeLoanPayment = getHomeLoanBeforePayment(state, year) * 12;
        } else if (year === 2) {
            results[year].annualHomeLoanPayment =
                (getHomeLoanBeforePayment(state, year) * 6) + (getHomeLoanAfterPayment(state, year) * 6)
                + getNYRebateTotal(state, 1) + getNYResiTaxCredit(state, 1) + getITC(state, 1);
        } else if (year <= mosaicTerm / 12) {
            results[year].annualHomeLoanPayment = getHomeLoanAfterPayment(state, year) * 12;
        }
        if (year === 1) {
            results[year].annualHomeLoanPaymentESS = getHomeLoanBeforePaymentESS(state, year) * 12;
        } else if (year === 2) {
            results[year].annualHomeLoanPaymentESS =
                (getHomeLoanBeforePaymentESS(state, year) * 6) + (getHomeLoanAfterPaymentESS(state, year) * 6)
                + getITCCashESS(state, 1);
        } else if (year <= mosaicTerm / 12) {
            results[year].annualHomeLoanPaymentESS = getHomeLoanAfterPaymentESS(state, year) * 12;
        }

        // ** MOSAIC ** //

        // ** NYSERDA ** //
        if (year === 1) {
            results[year].nyserdaRebate = getNYRebateTotal(state, year);
            results[year].nyResiTaxCredit = getNYResiTaxCredit(state, year);
        }

        if (year < 5) {
            results[year].cashNYAbatement = getNYPTaxAbatement(state, year) / 4;
            results[year].mosaicNYAbatement = getNYPTaxAbatementMosaic(state, year) / 4;
        } else {
            results[year].cashNYAbatement = 0;
            results[year].mosaicNYAbatement = 0;
        }


        // ** NYSERDA ** //


        if (year === 1) {
            results[year].cumulativeRecs = results[year].recs;
        } else {
            results[year].cumulativeRecs =
                results[year].recs + results[prevYear].cumulativeRecs;
        }

        if (year === 1) {
            results[year].cumulativeLoanSavings = results[year].annualLoanSavings;
        } else {
            results[year].cumulativeLoanSavings =
                results[year].annualLoanSavings + results[prevYear].cumulativeLoanSavings;
        }
        if (year === 1) {
            results[year].cumulativeMosaicSavings = results[year].annualMosaicSavings;
        } else {
            results[year].cumulativeMosaicSavings =
                results[year].annualMosaicSavings + results[prevYear].cumulativeMosaicSavings;
        }
        if (year === 1) {
            results[year].cumulativeSolarSavings = results[year].annualSolarSavings;
        } else {
            results[year].cumulativeSolarSavings =
                results[year].annualSolarSavings + results[prevYear].cumulativeSolarSavings;
        }
        //* SMART*//


        //* SMART*//

        //* OBS *//
        results[year].annualOBSPayment = getOBSAnnualPayment(state, year);
        results[year].annualOBSSavings = getAnnualOBSSavings(state, prevSolarValue, year);

        if (year === 1) {
            results[year].cumulativeOBSSavings = results[year].annualOBSSavings;
        } else {
            results[year].cumulativeOBSSavings =
                results[year].annualOBSSavings + results[prevYear].cumulativeOBSSavings;
        }

        //* OBS *//

        //* CAP *//
        if (year <= capTerm) {
            results[year].annualCAPPayment = getCAPAnnualPayment(state, year);
            results[year].annualCAPSavings = getAnnualCAPSavings(state, prevSolarValue, year);
            results[year].annualCAPInterestValue = getAnnualCAPIntDeductionValue(state, year);
        }

        if (year === 1) {
            results[year].cumulativeCAPSavings = results[year].annualCAPSavings;
        } else {
            results[year].cumulativeCAPSavings =
                results[year].annualCAPSavings + results[prevYear].cumulativeCAPSavings;
        }

        //* CAP *//

        //* Cumulative incentives *//

        results[year].annualIncentives = annualIncentives(state, year);

        if (year === 1) {
            results[year].cumulativeIncentives = results[year].annualIncentives;
        } else {
            results[year].cumulativeIncentives =
                results[year].annualIncentives + results[prevYear].cumulativeIncentives;
        }
        results[year].totalAnnualIncentives =
            annualIncentives(state, year);

        if (year === 1) {
            results[year].taxBenefits = itcBenefit(state, year) + getMACRS(state, year);
        }

        if (year === 1) {
            results[year].cumulativeTotalAnnualIncentives = results[year].totalAnnualIncentives + results[year].taxBenefits;
        } else {
            results[year].cumulativeTotalAnnualIncentives =
                results[year].totalAnnualIncentives + results[prevYear].cumulativeTotalAnnualIncentives;
        }

        //* Cumulative incentives *//

        let prevEnergyValue = year === 1 ? state.utility.energyValue : 0;
        if (energyValue > 0) {
            if (year > 1 && year <= horizonLength) {
                prevEnergyValue = results[prevYear].energyValue;
            }
            results[year].energyValue = annualEnergyValueForYear(year, prevEnergyValue, state);
            results[year].formattedAnnualBill = formattedAnnualBill(year, prevEnergyValue, state);
            results[year].annualBill = annualBill(year, prevEnergyValue, state);
            results[year].annualCashSavings = annualCashSavings(year, prevSolarValue, state);
            results[year].remainingBill =
                annualBill(year, prevEnergyValue, state)
                - annualSolarSavings(year, prevSolarValue, state);


            if (year === 1) {
                results[year].cumulativeCashSavings = results[year].annualCashSavings;
            } else {
                results[year].cumulativeCashSavings =
                    results[year].annualCashSavings + results[prevYear].cumulativeCashSavings;
            }
            if (year === 1) {
                results[year].UtilityBill = results[year].annualBill;
            } else {
                results[year].UtilityBill =
                    results[year].annualBill + results[prevYear].UtilityBill;
            }
        }

        let prevPpaRate = year === 1 ? state.ppa.rate : 0;
        if (selectedFinancing === 1 || selectedFinancing === 6) {
            if (year > 1 && year <= ppaYears) {
                prevPpaRate = results[prevYear].ppaRate;
            }
            results[year].ppaRate = ppaRateForYear(year, prevPpaRate, state);
            results[year].ppaBill = ppaBill(year, prevPpaRate, state);
            if (year === 1) {
                results[year].cummulativePPABill =
                results[year].ppaBill;
            } else {
                results[year].cummulativePPABill =
                    results[year].ppaBill + results[prevYear].cummulativePPABill;
            }
            results[year].formattedPPABill = formattedPPABill(year, prevPpaRate, state);
            results[year].annualPPASavings = annualPPASavings(
                state,
                year,
                prevPpaRate,
                prevSolarValue,
            );
            results[year].formattedAnnualPPASavings = formattedAnnualPPASavings(
                state,
                year,
                prevPpaRate,
                prevSolarValue,
            );
            results[year].ppaSavings = annualPPASavings(state, year, prevPpaRate, prevSolarValue);
            if (year === 1) {
                results[year].cumulativePPASavings = results[year].ppaSavings;
            } else {
                results[year].cumulativePPASavings =
                    results[year].ppaSavings + results[prevYear].cumulativePPASavings;
            }
        }
        if (year === 1) {
            results[year].itc = getITC(state, year);
            results[year].cashITC = getCashITC(state, year);
            results[year].cashprice = cashprice;
            results[year].formattedSystemPrice = formattedSystemPrice(state, year);
            results[year].macrs = getMACRS(state, year);
            results[year].year1CashReturned = getYear1CashReturned(state, year);
            results[year].sgip = selectSGIPIncentiveValue(state);
            results[year].ITCCashESS = getITCCashESS(state, 1);
        }

        if (year === ((obsTerm + 12) / 12)) {
            results[year].obsITC = getOBSITC(state, year);
        }
    }

    return results;
};

export const getAnnualResults = createSelector(buildResults, results => results);



// WEBPACK FOOTER //
// ./src/containers/Simulator/TabBar/Results/selectors.js